<script setup lang="ts">
import { computed } from 'vue'
import { Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions
} from 'chart.js'
import type { AlarmsByType } from '@/types/station'
import { useI18n } from '@/i18n'

const { t } = useI18n()

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend)

const props = defineProps<{
  alarms: AlarmsByType
}>()

// Alarm configuration - extensible for future types
const ALARM_CONFIG: Record<string, { label: string; color: string; description: string }> = {
  gfr: { label: 'GFR', color: '#ef4444', description: 'Ground Fault Relay' },
  ovpr: { label: 'OVPR', color: '#f97316', description: 'Over Voltage Protection' },
  uvpr: { label: 'UVPR', color: '#eab308', description: 'Under Voltage Protection' },
  // Future alarm types can be added here
  // ocr: { label: 'OCR', color: '#8b5cf6', description: 'Over Current Relay' },
}

// Calculate total alarms
const totalAlarms = computed(() => {
  return Object.values(props.alarms).reduce((sum, count) => sum + count, 0)
})

// สร้างข้อความสรุป alarm types
// const alarmSummaryText = computed(() => {
//   const parts: string[] = []
//   for (const [key, value] of Object.entries(props.alarms)) {
//     const config = ALARM_CONFIG[key]
//     if (config) {
//       parts.push(`${config.label}: ${value}`)
//     }
//   }
//   return parts.join(', ')
// })

// Check if all alarms are zero
const hasNoAlarms = computed(() => totalAlarms.value === 0)

// Prepare chart data
const chartData = computed<ChartData<'doughnut'>>(() => {
  const labels: string[] = []
  const data: number[] = []
  const colors: string[] = []

  // Only include alarm types that exist in the data
  for (const [key, value] of Object.entries(props.alarms)) {
    const config = ALARM_CONFIG[key]
    if (config) {
      labels.push(config.label)
      data.push(value)
      colors.push(config.color)
    }
  }

  // If no alarms, show a gray placeholder
  if (hasNoAlarms.value) {
    return {
      labels: [t('Normal')],
      datasets: [{
        data: [1],
        backgroundColor: ['#d1fae5'],
        borderWidth: 0,
      }]
    }
  }

  return {
    labels,
    datasets: [{
      data,
      backgroundColor: colors,
      borderWidth: 2,
      borderColor: '#ffffff',
      hoverOffset: 8,
    }]
  }
})

// Chart options with detailed tooltip
const chartOptions = computed<ChartOptions<'doughnut'>>(() => ({
  responsive: true,
  maintainAspectRatio: true,
  cutout: '60%',
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      bodyFont: { size: 12 },
      padding: 10,
      cornerRadius: 6,
      displayColors: false,
      callbacks: {
        title: () => '',
        label: (context) => {
          const label = context.label || ''
          const value = context.raw as number
          return `${label}: ${value}`
        }
      }
    }
  }
}))
</script>

<template>
  <div class="flex flex-col items-center justify-center">
    <!-- Donut Chart -->
    <div class="relative w-20 h-20">
      <Doughnut :data="chartData" :options="chartOptions" />
      <!-- Center text -->
      <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <template v-if="hasNoAlarms">
          <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span class="text-xs text-green-600 font-medium mt-1">{{ t('Normal') }}</span>
        </template>
        <template v-else>
          <span class="text-xl font-bold text-gray-800">{{ totalAlarms }}</span>
        </template>
      </div>
    </div>
  </div>
</template>
